/**
 * Utilidades para calcular y formatear tiempo estimado de entrega de resultados.
 * Issue #99: Exámenes derivados que tardan días en salir resultado.
 */

import type { TFunction } from "i18next";

/**
 * Formatea el tiempo estimado de entrega de un examen en texto legible.
 *
 * @param dias - Días hábiles para resultado (puede ser null/0)
 * @param horas - Horas adicionales para resultado (puede ser null/0)
 * @param t - Función de traducción i18next
 * @returns Texto formateado (ej: "3 días", "2 días 4 horas", "6 horas")
 */
export function formatTiempoEstimado(
  dias: number | null | undefined,
  horas: number | null | undefined,
  t: TFunction
): string {
  const d = dias ?? 0;
  const h = horas ?? 0;

  if (d === 0 && h === 0) return "";

  const partes: string[] = [];

  if (d > 0) {
    partes.push(t("muestras.validacion.dias_habiles", { count: d }));
  }

  if (h > 0) {
    partes.push(t("muestras.validacion.horas_adicionales", { count: h }));
  }

  return partes.join(" ");
}

/**
 * Calcula la fecha/hora estimada de entrega a partir de ahora.
 * Solo cuenta días hábiles (lunes a viernes).
 *
 * @param dias - Días hábiles para resultado
 * @param horas - Horas adicionales
 * @returns Fecha estimada de entrega
 */
export function calcularFechaEstimada(
  dias: number | null | undefined,
  horas: number | null | undefined
): Date {
  const d = dias ?? 0;
  const h = horas ?? 0;
  const ahora = new Date();

  // Sumar días hábiles
  let diasRestantes = d;
  const fecha = new Date(ahora);

  while (diasRestantes > 0) {
    fecha.setDate(fecha.getDate() + 1);
    const diaSemana = fecha.getDay();
    // Saltar fin de semana (0 = domingo, 6 = sábado)
    if (diaSemana !== 0 && diaSemana !== 6) {
      diasRestantes--;
    }
  }

  // Sumar horas adicionales
  fecha.setHours(fecha.getHours() + h);

  return fecha;
}

/**
 * Determina si un examen es "derivado" (tarda más de un día hábil).
 */
export function esExamenDerivado(
  dias: number | null | undefined,
  horas: number | null | undefined
): boolean {
  return (dias ?? 0) > 0 || (horas ?? 0) > 0;
}
